import React from "react";
import { each, size } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import Illustration, {
  IProps as IllustrationProps,
} from "@radarly/components/cards/card-edition/illustration";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { getColorFromTerm } from "@radarly/models/cards/dataviz";
import { Clause } from "@radarly/models/clause";
import { isTermClause } from "@radarly/models/clause/is";
import {
  Dataviz,
  isCloudDataviz,
  isHorizontalStackedBarChartDataviz,
  isKpiDataviz,
  isLineChartDataviz,
  isMapDataviz,
  isScatterPlotDataviz,
  isStackedBarChartDataviz,
} from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import {
  AIFragment,
  InfluencerFragment,
  SearchFragment,
  getTermFromAggs,
  isSearchFragmentDateMetrics,
  isSearchFragmentDateTerm,
  isSearchFragmentDemographic,
  isSearchFragmentGeo,
  isSearchFragmentPivot,
  isSearchFragmentTerm,
} from "@radarly/models/search";

interface FormatedFacetValue {
  id: number | string;
  label: string;
  color?: string;
}

interface FormatedFacet {
  id: string;
  type: string;
  mode: string;
  icon: string;
  label: string;
  values: FormatedFacetValue[];
  custom?: boolean;
  facetType?: string;
  canBeExcluded?: boolean;
}

export interface SharedCardsEditionProps {
  fragments: SearchFragment[] | [AIFragment] | [InfluencerFragment];
  dataviz: Dataviz;
}

function getColorFromTermAndDataviz(
  term: string,
  dataviz: Dataviz,
  facet: FullFacet
) {
  if (isHorizontalStackedBarChartDataviz(dataviz)) {
    return getColorFromTerm(
      dataviz.name,
      term === Facet.Tones ? Facet.Platforms : Facet.Tones
    );
  } else {
    return getColorFromTerm(dataviz.name, term, facet);
  }
}

function getIllustrationColor(
  fragment: SearchFragment | AIFragment | InfluencerFragment,
  dataviz: Dataviz,
  facets: Record<string, FormatedFacet>
) {
  // @ts-ignore
  if (dataviz.color && dataviz.color !== "") return dataviz.color;
  if (
    isSearchFragmentGeo(fragment) ||
    isSearchFragmentDemographic(fragment) ||
    isSearchFragmentTerm(fragment)
  ) {
    return getColorFromTermAndDataviz(
      getTermFromAggs(fragment.request.aggs),
      dataviz,
      facets[getTermFromAggs(fragment.request.aggs)]
    );
  } else if (isSearchFragmentPivot(fragment)) {
    const term = getTermFromAggs(fragment.request.aggs);
    if (isHorizontalStackedBarChartDataviz(dataviz)) {
      return getColorFromTerm(
        dataviz.name,
        getTermFromAggs(fragment.request.aggs[term].aggs)
      );
    } else {
      return getColorFromTerm(dataviz.name, term, facets[term]);
    }
  } else if (isSearchFragmentDateTerm(fragment)) {
    return getColorFromTermAndDataviz(
      getTermFromAggs(fragment.request.aggs.date.aggs),
      dataviz,
      facets[getTermFromAggs(fragment.request.aggs.date.aggs)]
    );
  } else {
    return "default";
  }
}

function getCountryFromClause(clause: Clause) {
  let country = null;
  each(clause.and, (innerClause) => {
    if (isTermClause(innerClause) && innerClause[Facet.Countries]) {
      const countries = innerClause[Facet.Countries];
      if (size(countries) === 1) {
        country = countries[0];
      }
    }
  });
  return country;
}

function getOptions(
  fragments: SearchFragment[] | [AIFragment] | [InfluencerFragment],
  dataviz: Dataviz
) {
  const options: IllustrationProps["options"] = {};
  const fragment = fragments[0];
  if (isKpiDataviz(dataviz)) {
    if (dataviz.displayDistributions) {
      options.showLine = true;
    }
  }
  if (isSearchFragmentGeo(fragment)) {
    if (isMapDataviz(dataviz)) {
      const term = getTermFromAggs(fragment.request.aggs);
      if (term === Facet.Towns) {
        options.towns = true;
      }
      if (getCountryFromClause(fragment.request.query)) {
        options.regions = true;
      }
    }
  }
  if (isScatterPlotDataviz(dataviz)) {
    options.scatterPlot = true;
  }
  if (isSearchFragmentTerm(fragment)) {
    const term = getTermFromAggs(fragment.request.aggs);
    if (isCloudDataviz(dataviz) && term === Facet.EmojiCharts) {
      options.emojis = true;
    }
  }
  if (isStackedBarChartDataviz(dataviz) && dataviz.normalized) {
    options.normalized = true;
  }
  if (isSearchFragmentDateMetrics(fragment)) {
    if (isLineChartDataviz(dataviz) && fragments.length > 1) {
      options.showPrevious = true;
    }
  }
  return options;
}

export default function SharedCardsEditionIllustration({
  fragments,
  dataviz,
}: SharedCardsEditionProps) {
  const fragment = fragments[0];
  const facets = useFacetsFromContext();
  return (
    <Illustration
      datavizName={dataviz.name}
      colorScheme={getIllustrationColor(fragment, dataviz, facets)}
      options={getOptions(fragments, dataviz)}
    />
  );
}
